import {
    CURRENCY,
    CreateProductInput,
    DimensionFormValues,
    DimensionKind,
    PriceFormValues,
    PriceInput,
    PricingDimensionInput,
    Product,
    ProductFormValues,
    UpdateProductInput,
} from '../types';

// Mutation payloads are built field by field from the form, never by spreading
// a record and never by deleting keys off one. A field the API does not take
// (created, updated, status, pricingSource, billingLink, version) therefore
// cannot reach the wire, whatever the form was seeded with. There is no
// tenantId in the client at all.

const buildPrice = (price: PriceFormValues): PriceInput => ({
    ...(price.term ? {term: price.term} : {}),
    amount: price.amount.trim(),
    // USD is the only currency the API accepts. The form shows it; it never
    // lets the operator pick another.
    currency: CURRENCY,
});

const buildDimension = (dimension: DimensionFormValues): PricingDimensionInput => {
    const description = dimension.description.trim();

    return {
        // The server assigns the id. A new unit has none; an edited one sends
        // back the one it was read with so the unit keeps its identity.
        ...(dimension.dimensionId ? {dimensionId: dimension.dimensionId} : {}),
        name: dimension.name.trim(),
        ...(description ? {description} : {}),
        unit: dimension.unit.trim(),
        kind: dimension.kind as DimensionKind,
        ...(dimension.usageSource
            ? {usageSource: {source: dimension.usageSource.source, meterId: dimension.usageSource.meterId}}
            : {}),
        prices: dimension.prices.map(buildPrice),
    };
};

export const buildCreateInput = (values: ProductFormValues): CreateProductInput => ({
    name: values.name.trim(),
    summary: values.summary.trim(),
    description: values.description.trim(),
    eulaUrl: values.eulaUrl.trim(),
    supportContact: values.supportContact.trim(),
    dimensions: values.dimensions.map(buildDimension),
});

// expectedVersion is the version the client READ. The API refuses a stale one
// with a ConflictError instead of overwriting someone else's edit.
export const buildUpdateInput = (record: Pick<Product, 'id' | 'version'>, values: ProductFormValues): UpdateProductInput => ({
    id: record.id,
    expectedVersion: record.version,
    ...buildCreateInput(values),
});
